import { notification } from 'antd';
import { useEffect, useState } from 'react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';

import {
  Button,
  FileDownload,
  FileUpload,
  Input
} from '@/components/Shared/ui';
import { autoCreditApi } from '@/integrations/app.api/auto-credit.api';
import { isValidUrl } from '@/services';
import { handleError } from '@/services/handleError';
import { useAppDispatch, useAppSelector } from '@/store';
import { setPageTitle } from '@/store/newOrder.slice';
import type { FileType } from '@/types/uploadFiles';

import useRefreshOrderData from '../../../hooks/useRefreshOrderData';

type TLeasingContractForm = {
  contractNumber: string;
  invoiceFile: FileType | undefined;
  salesDocumentSigningLink: string;
  vinCode: string;
};

const CurrentLeasingView: React.FC = () => {
  const dispatch = useAppDispatch();

  const { selectedLeasing } = useAppSelector((state) => state.financingStep);
  const { requestId } = useAppSelector((state) => state.newOrder);
  const { dicts } = useAppSelector((state) => state.dict);
  const { refreshOrderData } = useRefreshOrderData({
    allowWithOrder: true,
    autoFetch: false,
    requestId
  });

  const [throttleEnabled, setThrottleEnabled] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isFieldsFrozen, setIsFieldsFrozen] = useState(false);

  const { control, handleSubmit, formState, reset } =
    useForm<TLeasingContractForm>({
      mode: 'all',
      delayError: 200
    });

  const isSubmitDisabled =
    throttleEnabled ||
    Object.values(formState.errors).length > 0 ||
    !selectedLeasing;

  const sendDocumentsHandler: SubmitHandler<TLeasingContractForm> = async (
    data
  ) => {
    if (!selectedLeasing || !dicts) return;

    setIsSubmitLoading(true);
    try {
      if (!data.invoiceFile?.base64) {
        notification.warning({
          message: 'Необходимо загрузить файл с счетом на оплату'
        });
        return;
      }

      await autoCreditApi.sendLeasingContractData(selectedLeasing.uuid, {
        contractNumber: data.contractNumber,
        invoiceFile: data.invoiceFile.base64,
        salesDocumentSigningLink: data.salesDocumentSigningLink,
        vin_code: data.vinCode
      });

      notification.success({
        title: 'Успешно',
        description: 'Данные были успешно отправлены',
        duration: 5
      });

      await refreshOrderData(requestId);

      setThrottleEnabled(true);
      setTimeout(() => setThrottleEnabled(false), 10000);
    } catch (error) {
      handleError(error, {
        customTitle: 'Ошибка при отправке данных',
        customDescription: 'Пожалуйста попробуйте позже'
      });
    } finally {
      setIsSubmitLoading(false);
    }
  };

  useEffect(() => {
    dispatch(setPageTitle('Финансирование'));
  }, []);

  useEffect(() => {
    const {
      invoiceLink,
      purchaseContractNumber,
      salesDocumentSigningLink,
      status,
      vinCode
    } = selectedLeasing || {};

    setIsFieldsFrozen(
      status?.code === 'financed' ||
        !!invoiceLink ||
        !!purchaseContractNumber ||
        !!salesDocumentSigningLink ||
        !!vinCode
    );

    if (!selectedLeasing) {
      return;
    }

    reset({
      contractNumber: purchaseContractNumber || '',
      salesDocumentSigningLink: salesDocumentSigningLink || '',
      vinCode: vinCode || ''
    });
  }, [selectedLeasing]);

  return (
    <form
      className="financing-step__form"
      onSubmit={handleSubmit(sendDocumentsHandler)}
    >
      <div className="financing-step__field">
        <Controller
          control={control}
          name="contractNumber"
          rules={{
            required: 'Пожалуйста, введите номер контракта',
            validate: (value) => {
              if (!value) return 'Пожалуйста, введите номер контракта';
            }
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              label="Номер ДКП"
              error={error?.message}
              isRequired
              isFrozen={isFieldsFrozen}
              onChange={(e) => {
                // Допустимые знаки: любые буквы, цифры, тире, подчёркивание
                field.onChange(
                  e.target.value.replace(/[^a-zA-Zа-яА-ЯёЁ0-9\-_.\s]/g, '')
                );
              }}
            />
          )}
        />
      </div>

      <div className="financing-step__field">
        <Controller
          control={control}
          name="salesDocumentSigningLink"
          rules={{
            required: 'Пожалуйста, введите ссылку',
            validate: (value) => {
              if (!value) return 'Пожалуйста, введите ссылку';
              if (!isValidUrl(value)) {
                return 'Некорректная ссылка';
              }
            }
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              type="url"
              label="Ссылка на подписание 4-х стороннего ДКП"
              error={error?.message}
              isRequired
              isFrozen={isFieldsFrozen}
            />
          )}
        />
      </div>

      <div className="financing-step__field">
        <Controller
          control={control}
          name="vinCode"
          rules={{
            required: 'Пожалуйста, введите данные',
            minLength: {
              value: 17,
              message: 'VIN код должен состоять из 17 символов'
            },
            validate: (value) => {
              if (/I|O|Q/.test(value))
                return 'В поле VIN не должно быть букв I, O, Q';
            }
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              label="VIN"
              value={field.value ?? ''}
              onChange={(e) => {
                const value = e.target.value.replace(/[^A-Za-z0-9]/g, '');
                if (value.length > 17) return;
                field.onChange(value.toUpperCase());
              }}
              error={error?.message}
              isFrozen={isFieldsFrozen}
              isRequired
            />
          )}
        />
      </div>

      <div className="financing-step__field">
        {selectedLeasing?.invoiceLink ? (
          <FileDownload
            fileLink={selectedLeasing.invoiceLink}
            prevStateTitle="Счет на оплату"
            oldDownloadMethod
          />
        ) : (
          <Controller
            control={control}
            name="invoiceFile"
            rules={{
              validate: (value) => {
                if (!value?.base64) return 'Пожалуйста, выберите файл';
                if ('size' in value && (value?.size || 0) > 1 * 1024 * 1024)
                  return 'Файл не должен превышать 1 МБ';
              }
            }}
            render={({ field, fieldState: { error } }) => (
              <FileUpload
                title="Счет на оплату"
                file={field.value}
                onChange={field.onChange}
                onDelete={() => field.onChange(undefined)}
                error={error?.message}
                isRequired
                accept=".pdf"
                allowedExtensions={['pdf']}
                isFrozen={isFieldsFrozen}
              />
            )}
          />
        )}
      </div>

      {!isFieldsFrozen && (
        <Button
          className="financing-step__submit"
          variant="filled-red"
          disabled={isSubmitDisabled}
          isLoading={isSubmitLoading}
        >
          Отправить документы
        </Button>
      )}
    </form>
  );
};

export default CurrentLeasingView;
